// /Users/pramod/Desktop/school-erp/frontend/src/Head-Of-Institution/LiveMonitoring/components/ClassSearchView.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Search, 
  Layers, 
  Calendar, 
  Users, 
  BookOpen, 
  X,
  Filter,
  GraduationCap,
  Eye,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import api from '../../../api';

const ClassSearchView = ({ onClassSelect, selectedClass, tenantId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDepartment] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [expandedClass, setExpandedClass] = useState(null);
  const searchInputRef = useRef(null);

  const fetchClasses = useCallback(async (showLoading = true) => {
    if (!tenantId) return;
    try {
      if (showLoading) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      const response = await api.get(`/api/head/active-classes?tenantId=${tenantId}`);
      if (response.data.success) {
        const classData = response.data.data.classes;
        setClasses(classData);
        setFilteredClasses(classData);
      }
    } catch (err) {
      console.error('Error fetching classes:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchClasses(true);
  }, [fetchClasses]);

  useEffect(() => {
    let filtered = [...classes];
    
    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(cls => 
        cls.name?.toLowerCase().includes(term) ||
        cls.term?.name?.toLowerCase().includes(term) ||
        cls.semester?.name?.toLowerCase().includes(term) ||
        cls.academicYear?.name?.toLowerCase().includes(term)
      );
    }
    
    // Type filter
    if (filterType === 'term') {
      filtered = filtered.filter(cls => !cls.isSemesterBased);
    } else if (filterType === 'semester') {
      filtered = filtered.filter(cls => cls.isSemesterBased);
    }
    
    // Department filter
    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(cls => cls.departmentId === selectedDepartment);
    }
    
    // Sort
    if (sortBy === 'name') {
      filtered.sort((a, b) => a.name?.localeCompare(b.name) || 0);
    } else if (sortBy === 'facultyCount') {
      filtered.sort((a, b) => (b.facultyCount || 0) - (a.facultyCount || 0));
    } else if (sortBy === 'subjectCount') {
      filtered.sort((a, b) => (b.subjectCount || 0) - (a.subjectCount || 0));
    }
    
    setFilteredClasses(filtered);
  }, [searchTerm, classes, filterType, selectedDepartment, sortBy]);

  const handleClassClick = (classItem) => {
    if (selectedClass?.id === classItem.id) {
      onClassSelect(null);
      setExpandedClass(null);
    } else {
      onClassSelect(classItem);
      setExpandedClass(classItem.id);
    }
  };

  const toggleExpand = (classId, e) => {
    e.stopPropagation();
    setExpandedClass(expandedClass === classId ? null : classId);
  };

  const getContextBadge = (classItem) => {
    if (classItem.isSemesterBased && classItem.semester) {
      return {
        label: `Semester ${classItem.semester.number}`,
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        icon: <GraduationCap className="w-3 h-3" />
      };
    } else if (classItem.term) {
      return {
        label: classItem.term.name || `Term ${classItem.term.number}`,
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        icon: <Calendar className="w-3 h-3" />
      };
    }
    return { 
      label: 'N/A', 
      color: 'bg-gray-100 text-gray-600 border-gray-200',
      icon: null
    };
  };

  const getStatusBadge = (classItem) => {
    if (classItem.academicYear?.status === 'active') {
      return {
        label: 'Active Today',
        color: 'bg-green-50 text-green-700 border-green-200',
        icon: <CheckCircle className="w-3 h-3" />
      };
    }
    return {
      label: 'Inactive',
      color: 'bg-gray-50 text-gray-500 border-gray-200',
      icon: <AlertCircle className="w-3 h-3" />
    };
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Quick stats calculations
  const totalClasses = classes.length;
  const activeClasses = classes.filter(c => c.academicYear?.status === 'active').length;
  const semesterClasses = classes.filter(c => c.isSemesterBased).length;
  const termClasses = classes.filter(c => !c.isSemesterBased).length;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col w-full">
      
      {/* ===== PANEL CONTROLS HEADER ===== */}
      <div className="p-4 border-b border-gray-100 bg-gray-50/50 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 bg-indigo-50 rounded-xl text-indigo-600 flex-shrink-0">
              <Layers className="w-4 h-4" />
            </div>
            <div className="truncate">
              <h3 className="text-sm font-bold text-gray-900 tracking-tight">Active Class Directory</h3>
              <p className="text-[11px] text-gray-400 font-medium">Filtered down: {filteredClasses.length} options</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button 
              type="button"
              onClick={() => setShowFilters(!showFilters)} 
              className={`p-2 rounded-xl transition-all ${
                showFilters ? 'bg-indigo-600 text-white shadow-xs' : 'hover:bg-gray-200/60 text-gray-500'
              }`}
              title="Toggle filter controls drawer"
            >
              <Filter className="w-4 h-4" />
            </button>
            <button 
              type="button"
              onClick={() => fetchClasses(false)} 
              className="p-2 hover:bg-gray-200/60 rounded-xl transition-colors text-gray-400"
              disabled={refreshing}
              title="Refresh item list records"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Dynamic Search Box Entry */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search classes, rooms, terms..."
            className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white font-medium"
          />
          {searchTerm && (
            <button 
              type="button"
              onClick={() => setSearchTerm('')} 
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full text-gray-400"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Horizontal Swipeable Summary Metric Elements */}
        <div className="flex items-center gap-3 overflow-x-auto text-[11px] font-medium text-gray-500 pb-1 hide-scrollbar whitespace-nowrap">
          <span>Total: <strong className="text-gray-800">{totalClasses}</strong></span>
          <span className="text-gray-200">•</span>
          <span className="flex items-center gap-1">Running: <strong className="text-green-600">{activeClasses}</strong></span>
          <span className="text-gray-200">•</span>
          <span className="flex items-center gap-0.5">Semesters: <strong className="text-blue-600">{semesterClasses}</strong></span>
          <span className="text-gray-200">•</span>
          <span className="flex items-center gap-0.5">Terms: <strong className="text-purple-600">{termClasses}</strong></span>
        </div>

        {/* Collapsible Filter Sub-panel Drawer */}
        {showFilters && (
          <div className="p-3 bg-white rounded-xl border border-gray-200 space-y-3 shadow-xs animate-fade-in text-xs font-semibold">
            {/* Filter classification paths */}
            <div className="space-y-1">
              <span className="text-[10px] text-gray-400 uppercase font-bold tracking-wide block">Structure Type</span>
              <div className="flex flex-wrap gap-1.5">
                {['all', 'term', 'semester'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setFilterType(type)}
                    className={`px-3 py-1 rounded-lg border uppercase tracking-wider text-[10px] transition-colors ${
                      filterType === type 
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs' 
                        : 'bg-gray-50 text-gray-600 border-gray-200 active:bg-gray-100'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Sorting Classification Categories */}
            <div className="space-y-1">
              <span className="text-[10px] text-gray-400 uppercase font-bold tracking-wide block">Sort Criteria</span>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { key: 'name', label: 'Alphabetical' },
                  { key: 'facultyCount', label: 'Teachers Count' },
                  { key: 'subjectCount', label: 'Subjects Count' }
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-1 rounded-lg border text-[10px] font-bold transition-colors ${
                      sortBy === opt.key 
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs' 
                        : 'bg-gray-50 text-gray-600 border-gray-200 active:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions strip inside filter box */}
            <div className="pt-2 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="text-indigo-600 hover:text-indigo-800 text-[11px] font-bold"
              >
                Reset Search Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== MAIN VALUE SCROLL Feeds LISTING ===== */}
      <div className="max-h-[380px] overflow-y-auto p-2 space-y-2 bg-gray-50/30">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-100 border-t-indigo-600" />
            <p className="text-xs text-gray-400 font-medium mt-2">Loading data sheets...</p>
          </div>
        ) : filteredClasses.length === 0 ? (
          <div className="text-center py-10 px-4 bg-white rounded-xl border border-gray-100">
            <Layers className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm font-bold text-gray-600">No matching classes mapped</p>
            <p className="text-xs text-gray-400 mt-0.5">Try altering the words or clearing filters.</p>
            {searchTerm && (
              <button 
                type="button"
                onClick={() => setSearchTerm('')}
                className="mt-3 text-xs bg-indigo-50 text-indigo-700 font-bold px-3 py-1.5 rounded-xl border border-indigo-100"
              >
                Reset Search Text
              </button>
            )}
          </div>
        ) : (
          filteredClasses.map((classItem) => {
            const contextBadge = getContextBadge(classItem);
            const statusBadge = getStatusBadge(classItem);
            const isSelected = selectedClass?.id === classItem.id;
            const isExpanded = expandedClass === classItem.id;

            return (
              <div
                key={classItem.id}
                className={`rounded-xl border transition-all duration-150 overflow-hidden ${
                  isSelected 
                    ? 'border-indigo-400 ring-2 ring-indigo-50 bg-indigo-50/20' 
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleClassClick(classItem)}
                  onKeyDown={(e) => e.key === 'Enter' && handleClassClick(classItem)}
                  className="w-full p-3.5 select-none focus:outline-none"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <h4 className={`text-sm font-bold tracking-tight truncate ${isSelected ? 'text-indigo-700' : 'text-gray-800'}`}>
                          {classItem.name}
                        </h4>
                        <span className={`px-2 py-0.2 rounded text-[9px] font-bold border inline-flex items-center gap-0.5 ${contextBadge.color}`}>
                          {contextBadge.icon}
                          {contextBadge.label}
                        </span>
                        <span className={`px-2 py-0.2 rounded text-[9px] font-bold border inline-flex items-center gap-0.5 ${statusBadge.color}`}>
                          {statusBadge.icon}
                          {statusBadge.label}
                        </span>
                      </div>

                      {/* Brief numbers list metrics layout strip */}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 font-medium pt-1">
                        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-gray-400" /> {classItem.facultyCount || 0} Teachers</span>
                        <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5 text-gray-400" /> {classItem.subjectCount || 0} Topics</span>
                        <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5 text-gray-400" /> {classItem.labBatchCount || 0} Practicals</span>
                      </div>

                      {/* Advanced drop details inside mapping panels */}
                      {isExpanded && (
                        <div className="mt-3 pt-3 border-t border-gray-200/60 grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-gray-600 font-semibold animate-fade-in">
                          <div>
                            <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Calendar Term Context</span>
                            <span>{classItem.academicYear?.name || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Status Index</span>
                            <span className={classItem.academicYear?.status === 'active' ? 'text-green-700' : 'text-gray-500'}>
                              {classItem.academicYear?.status === 'active' ? 'Operational' : 'Idle'}
                            </span>
                          </div>
                          {classItem.semester && (
                            <>
                              <div>
                                <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Semester Level</span>
                                <span>{classItem.semester.name}</span>
                              </div>
                              <div>
                                <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Tracking Classification</span>
                                <span>{classItem.semester.type || 'Standard'}</span>
                              </div>
                            </>
                          )}
                          {classItem.term && (
                            <>
                              <div>
                                <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Term Set</span>
                                <span>{classItem.term.name}</span>
                              </div>
                              <div>
                                <span className="block text-[10px] text-gray-400 font-medium uppercase tracking-wider">Index Slot</span>
                                <span>No. {classItem.term.number || 'N/A'}</span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Action toggles element anchors right */}
                    <div className="flex flex-col items-end justify-between self-stretch flex-shrink-0">
                      <Eye className={`w-4 h-4 ${isSelected ? 'text-indigo-600' : 'text-gray-300'}`} />
                      <button
                        type="button"
                        onClick={(e) => toggleExpand(classItem.id, e)}
                        className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 mt-auto active:scale-95 transition-transform"
                        aria-label="Toggle details view area"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ===== SUMMARY FOOTER FOOTNOTE BAND ===== */}
      <div className="p-3 border-t border-gray-100 bg-gray-50 text-[11px] text-gray-400 flex items-center justify-between flex-wrap gap-2 font-medium">
        <span>Active list: {filteredClasses.length} shown</span>
        <button 
          type="button"
          onClick={() => fetchClasses(false)}
          className="text-indigo-600 font-bold hover:underline inline-flex items-center gap-0.5"
        >
          Force Reload
        </button>
      </div>

    </div>
  );
};

export default ClassSearchView;